import { useState } from 'react';
import { companyInfo } from '@/mocks/multifierros';

export default function ContactSection() {
  const [charCount, setCharCount] = useState(0);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('Solicitud enviada. Nuestro equipo te contactará en menos de 24 horas.');
      form.reset();
      setCharCount(0);
      setTimeout(() => setFormStatus('idle'), 6000);
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9keahmc26n1c7c5qhfg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('Solicitud enviada correctamente. Nuestro equipo te contactará en menos de 24 horas.');
        form.reset();
        setCharCount(0);
      } else {
        setFormStatus('error');
        const msg = typeof serverMsg === 'string' && serverMsg.includes('spam')
          ? 'No se pudo enviar la solicitud. Por favor intenta nuevamente.'
          : (typeof serverMsg === 'string' ? serverMsg : 'Error al enviar. Intenta nuevamente.');
        setFormMessage(msg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('Error de conexión. Por favor intenta nuevamente.');
    }

    setTimeout(() => setFormStatus('idle'), 7000);
  };

  return (
    <section id="contacto" className="py-16 md:py-20 bg-background-50">
      <div className="max-w-[960px] mx-auto px-4 md:px-8">
        <h2 className="font-heading text-4xl md:text-5xl text-foreground-50 tracking-wide text-center mb-8">
          Contacto
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
          {/* Info */}
          <div className="space-y-5">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-background-200 rounded-sm flex-shrink-0">
                <i className="ri-map-pin-line text-foreground-200 text-sm"></i>
              </div>
              <div>
                <p className="text-foreground-400 text-xs font-bold uppercase tracking-widest mb-0.5">DIRECCIÓN</p>
                <p className="text-foreground-200 text-sm">{companyInfo.address}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-background-200 rounded-sm flex-shrink-0">
                <i className="ri-phone-line text-foreground-200 text-sm"></i>
              </div>
              <div>
                <p className="text-foreground-400 text-xs font-bold uppercase tracking-widest mb-0.5">TELÉFONOS</p>
                {companyInfo.phones.map((ph) => (
                  <a key={ph} href={`tel:${ph.replace(/\s/g, '')}`} className="block text-foreground-200 hover:text-foreground-50 text-sm transition-colors">
                    {ph}
                  </a>
                ))}
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-background-200 rounded-sm flex-shrink-0">
                <i className="ri-mail-line text-foreground-200 text-sm"></i>
              </div>
              <div>
                <p className="text-foreground-400 text-xs font-bold uppercase tracking-widest mb-0.5">EMAIL</p>
                {companyInfo.emails.map((em) => (
                  <a key={em} href={`mailto:${em}`} className="block text-foreground-200 hover:text-foreground-50 text-sm transition-colors">
                    {em}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            data-readdy-form
            className="bg-background-100 rounded-sm border border-background-300/40 p-5 md:p-6"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div className="flex flex-col gap-1">
                <label htmlFor="nombre" className="text-foreground-400 text-xs font-bold uppercase tracking-widest">NOMBRE *</label>
                <input type="text" id="nombre" name="nombre" required placeholder="Tu nombre" className="bg-background-50 border border-background-300/40 rounded-sm px-3 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors" />
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="empresa" className="text-foreground-400 text-xs font-bold uppercase tracking-widest">EMPRESA</label>
                <input type="text" id="empresa" name="empresa" placeholder="Tu empresa" className="bg-background-50 border border-background-300/40 rounded-sm px-3 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div className="flex flex-col gap-1">
                <label htmlFor="email" className="text-foreground-400 text-xs font-bold uppercase tracking-widest">EMAIL *</label>
                <input type="email" id="email" name="email" required placeholder="tu@empresa.cl" className="bg-background-50 border border-background-300/40 rounded-sm px-3 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors" />
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="telefono" className="text-foreground-400 text-xs font-bold uppercase tracking-widest">TELÉFONO</label>
                <input type="tel" id="telefono" name="telefono" placeholder="+56 9 XXXX XXXX" className="bg-background-50 border border-background-300/40 rounded-sm px-3 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors" />
              </div>
            </div>
            <div className="mb-3 flex flex-col gap-1">
              <label htmlFor="servicio" className="text-foreground-400 text-xs font-bold uppercase tracking-widest">SERVICIO</label>
              <select id="servicio" name="servicio" className="bg-background-50 border border-background-300/40 rounded-sm px-3 py-2.5 text-sm text-foreground-50 focus:outline-none focus:border-primary-400 transition-colors cursor-pointer">
                <option value="">Selecciona un servicio</option>
                <option value="Marcos Viga">Marcos Viga</option>
                <option value="Marcos Reticulados">Marcos Reticulados</option>
                <option value="Barras Integrales y Pernos">Barras Integrales y Pernos</option>
                <option value="Tuercas y Placas">Tuercas, Placas y Acoples</option>
                <option value="Corte Plasma CNC">Corte Plasma CNC</option>
                <option value="Oxicorte de Planchas">Oxicorte de Planchas</option>
                <option value="Otro">Otro</option>
              </select>
            </div>
            <div className="mb-4 flex flex-col gap-1">
              <label htmlFor="mensaje" className="text-foreground-400 text-xs font-bold uppercase tracking-widest">MENSAJE *</label>
              <textarea id="mensaje" name="mensaje" required rows={4} maxLength={500} onChange={(e) => setCharCount(e.target.value.length)} placeholder="Describe tu proyecto..." className="bg-background-50 border border-background-300/40 rounded-sm px-3 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors resize-none"></textarea>
              <div className="flex justify-between">
                <p className="text-foreground-500 text-xs">Respondemos en menos de 24h.</p>
                <span className="text-foreground-500 text-xs">{charCount}/500</span>
              </div>
            </div>

            <div className="website-alt-wrapper">
              <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
            </div>

            <button type="submit" className="w-full bg-primary-500 hover:bg-primary-600 text-white py-3 text-xs font-bold uppercase tracking-[0.15em] rounded-sm transition-colors whitespace-nowrap cursor-pointer">
              Enviar Solicitud
            </button>

            {formStatus !== 'idle' && (
              <div className={`mt-3 p-3 rounded-sm text-sm font-medium ${formStatus === 'success' ? 'bg-background-200 border border-background-300 text-foreground-200' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                {formMessage}
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}